import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import api from "api";
import { toast } from "react-toastify";
import { getMediaUrl } from "config";
import ShareBatchModal from "./ShareBatchModal";

/** Format a number or numeric string with commas (e.g. 20000 → "20,000"). Returns "" for falsy. */
const fmtNum = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    return Number(String(v).replace(/\D/g, "")).toLocaleString();
};

const BatchCard = ({ batch, order, getOrders, onConfirmDelete, handleExportorderBatchcode, allOrders }) => {
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState("saved"); // "saved" | "unsaved" | "saving"
    const [shareOpen, setShareOpen] = useState(false);

    const isPending = batch?.status === "pending" || !batch?.status;
    const isBlank = !batch?.product_name;

    // Build deduplicated list of previous products from same brand (cross-order)
    const previousProducts = useMemo(() => {
        if (!allOrders || !order?.brandId?._id) return [];
        const brandId = order.brandId._id;
        const seen = new Set();
        const products = [];
        for (const o of allOrders) {
            if (String(o?.brandId?._id) !== String(brandId)) continue;
            for (const b of (o.batchIds || [])) {
                const name = b?.product_name;
                if (!name || seen.has(name)) continue;
                seen.add(name);
                products.push({
                    name,
                    description: b?.text || "",
                    image: b?.product_img || null,
                    coaLink: b?.coaLink || "",
                    rewardPoints: b?.rewardPoints ?? "",
                });
            }
        }
        return products;
    }, [allOrders, order]);

    const handleCopyFrom = (e) => {
        const productName = e.target.value;
        if (!productName) return;
        const prev = previousProducts.find((p) => p.name === productName);
        if (prev) {
            dirtyRef.current = true;
            setSaveStatus("unsaved");
            setData((d) => ({
                ...d,
                productName: prev.name,
                description: prev.description,
                coaLink: prev.coaLink,
                rewardPoints: prev.rewardPoints,
            }));
            if (prev.image) {
                batch.product_img = prev.image;
            }
        }
    };

    const [data, setData] = useState({
        productName: batch?.product_name ?? "",
        number_of_codes: batch?.number_of_codes ?? "",
        generated_code_by_increase: batch?.generated_code_by_increase ?? "",
        rewardPoints: batch?.rewardPoints ?? "",
        coaLink: batch?.coaLink ?? "",
        description: batch?.text ?? "",
    });

    const saveRef = useRef(null);
    const dataRef = useRef(data);
    const fileRef = useRef(file);
    const dirtyRef = useRef(false);

    // Keep refs in sync
    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => { fileRef.current = file; }, [file]);

    const handleChange = (e) => {
        dirtyRef.current = true;
        setSaveStatus("unsaved");
        if (["rewardPoints", "generated_code_by_increase", "number_of_codes"].includes(e.target.name)) {
            setData((prev) => ({
                ...prev,
                [e.target.name]: Number(e.target.value.replace(/\D/g, "")),
            }));
        } else {
            setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    const handleFileSelect = (selectedFile) => {
        if (!selectedFile) return;
        if (selectedFile.size > 5 * 1024 * 1024) {
            return toast.error("Image must be under 5MB");
        }
        if (!selectedFile.type.startsWith("image/")) {
            return toast.error("File must be an image");
        }
        setFile(selectedFile);
        dirtyRef.current = true;
        setSaveStatus("unsaved");
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const save = useCallback((currentData, currentFile) => {
        if (currentData.coaLink && !/^https?:\/\//.test(currentData.coaLink)) {
            return;
        }
        if (!dirtyRef.current) return;

        setSaving(true);
        setSaveStatus("saving");
        const formdata = new FormData();
        formdata.append("product_name", currentData.productName);
        formdata.append("rewardPoints", currentData.rewardPoints);
        formdata.append("product_img", currentFile ?? batch?.product_img ?? "");
        formdata.append("text", currentData.description);
        formdata.append("generated_code_by_increase", currentData.generated_code_by_increase);
        formdata.append("number_of_codes", currentData.number_of_codes);
        formdata.append("coaLink", currentData.coaLink);

        api("put", `/requests/${batch._id}`, formdata)
            .then(() => {
                setSaving(false);
                setSaveStatus("saved");
                dirtyRef.current = false;
            })
            .catch((err) => {
                setSaving(false);
                setSaveStatus("unsaved");
                toast.error(err?.response?.data?.message || "Failed to save batch");
            });
    }, [batch]);

    // Save on blur — when user clicks out of any field
    const handleBlur = () => {
        if (dirtyRef.current) {
            clearTimeout(saveRef.current);
            save(data, file);
        }
    };

    // Auto-save on image change (immediate)
    useEffect(() => {
        if (file) {
            clearTimeout(saveRef.current);
            save(data, file);
        }
        // eslint-disable-next-line
    }, [file]);

    // Flush unsaved changes on unmount (batch card collapsing)
    useEffect(() => {
        return () => {
            clearTimeout(saveRef.current);
            if (dirtyRef.current) {
                // Fire and forget — component is unmounting
                const currentData = dataRef.current;
                const currentFile = fileRef.current;
                if (currentData.coaLink && !/^https?:\/\//.test(currentData.coaLink)) return;

                const formdata = new FormData();
                formdata.append("product_name", currentData.productName);
                formdata.append("rewardPoints", currentData.rewardPoints);
                formdata.append("product_img", currentFile ?? batch?.product_img ?? "");
                formdata.append("text", currentData.description);
                formdata.append("generated_code_by_increase", currentData.generated_code_by_increase);
                formdata.append("number_of_codes", currentData.number_of_codes);
                formdata.append("coaLink", currentData.coaLink);
                api("put", `/requests/${batch._id}`, formdata).catch(() => {});
            }
        };
        // eslint-disable-next-line
    }, []);

    const handleSave = () => {
        clearTimeout(saveRef.current);
        save(data, file);
    };

    const imagePreview = file
        ? URL.createObjectURL(file)
        : batch?.product_img
            ? getMediaUrl(batch.product_img)
            : null;

    const codeType = batch?.code_type || order?.code_type;

    const statusColor =
        batch?.status === "approved" ? "#00B207" :
        batch?.status === "processing" ? "#362FD8" :
        "red";

    const statusLabel =
        batch?.status?.charAt(0).toUpperCase() + (batch?.status?.slice(1) || "") || "Pending";

    const codeTypeStyle = {
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 500,
        backgroundColor:
            codeType === "nfc" ? "rgba(54,47,216,0.1)" :
            codeType === "codeless" ? "rgba(255,152,0,0.1)" :
            "rgba(0,178,7,0.1)",
        color:
            codeType === "nfc" ? "#362FD8" :
            codeType === "codeless" ? "#E65100" :
            "#00B207",
    };

    const codeTypeLabel =
        codeType === "nfc" ? "NFC" :
        codeType === "codeless" ? "Codeless" :
        codeType === "regular" ? "Regular" : "\u2014";

    // Save button styling based on state
    const saveButtonStyle = {
        width: "auto",
        padding: "6px 20px",
        transition: "all 0.2s",
    };

    const saveButtonLabel =
        saveStatus === "saving" ? "Saving..." :
        saveStatus === "unsaved" ? "Save" :
        "\u2713 Saved";

    const saveButtonClass =
        saveStatus === "unsaved" ? "vs-btn-save vs-btn-unsaved" :
        saveStatus === "saving" ? "vs-btn-save vs-btn-saving" :
        "vs-btn-save vs-btn-saved";

    return (
        <div className="vs-batch-card" onBlur={handleBlur}>
            <div style={{ display: "flex", gap: "16px" }}>
                {/* Drag & Drop Image Area */}
                <div
                    className={`vs-batch-dropzone${dragging ? " dragging" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) => {
                            if (e.target.files.length) handleFileSelect(e.target.files[0]);
                        }}
                    />
                    {imagePreview ? (
                        <img src={imagePreview} alt={data.productName || "Product"} />
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="4" />
                            <path d="M8 17l3-4 2.5 3 3.5-4.5 4 5.5H3z" />
                            <circle cx="8.5" cy="8.5" r="2" />
                        </svg>
                    )}
                </div>

                {/* Fields Grid */}
                <div className="vs-batch-fields">
                    {/* Copy from previous — only shown on blank batches with available products */}
                    {isBlank && previousProducts.length > 0 && (
                        <div className="vs-batch-field" style={{ gridColumn: "1 / -1" }}>
                            <label style={{ color: "#362FD8", fontWeight: 600, fontSize: 12 }}>Copy from previous product</label>
                            <select
                                onChange={handleCopyFrom}
                                defaultValue=""
                                style={{
                                    backgroundColor: "#F3F3F3",
                                    border: "1px dashed #362FD8",
                                    borderRadius: "6px",
                                    padding: "6px 10px",
                                    fontSize: 13,
                                    width: "100%",
                                    cursor: "pointer",
                                    color: "#333",
                                }}
                            >
                                <option value="">— Select to pre-fill —</option>
                                {previousProducts.map((p) => (
                                    <option key={p.name} value={p.name}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="vs-batch-field">
                        <label>Product Name <span className="vs-char-count">{data.productName?.length || 0}/120</span></label>
                        <input
                            type="text"
                            name="productName"
                            value={data.productName}
                            onChange={handleChange}
                            placeholder="Enter product name"
                            maxLength={120}
                        />
                    </div>

                    <div className="vs-batch-field">
                        <label>Allocated Codes</label>
                        <input
                            type="text"
                            name="number_of_codes"
                            value={fmtNum(data.number_of_codes)}
                            onChange={handleChange}
                            placeholder="e.g. 10,000"
                        />
                    </div>

                    <div className="vs-batch-field">
                        <label>Generated Code Count</label>
                        <input
                            type="text"
                            name="generated_code_by_increase"
                            value={fmtNum(data.generated_code_by_increase)}
                            onChange={handleChange}
                            placeholder="e.g. 15,000"
                            disabled={!isPending}
                        />
                    </div>

                    <div className="vs-batch-field">
                        <label>Reward Points</label>
                        <input
                            type="text"
                            name="rewardPoints"
                            value={data.rewardPoints}
                            onChange={handleChange}
                            placeholder="0"
                            maxLength={3}
                        />
                    </div>

                    <div className="vs-batch-field full-width">
                        <label>COA Link <span className="vs-char-count">{data.coaLink?.length || 0}/250</span></label>
                        <input
                            type="text"
                            name="coaLink"
                            value={data.coaLink}
                            onChange={handleChange}
                            placeholder="https://example.com/coa.pdf"
                            maxLength={250}
                        />
                    </div>

                    <div className="vs-batch-field full-width">
                        <label>Description <span className="vs-char-count">{data.description?.length || 0}/120</span></label>
                        <textarea
                            name="description"
                            value={data.description}
                            onChange={handleChange}
                            placeholder="Describe the product batch"
                            maxLength={120}
                        />
                    </div>
                </div>
            </div>

            {/* Actions Row */}
            <div className="vs-batch-actions">
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span className="vs-batch-status">
                        <span className="dot" style={{ backgroundColor: statusColor }} />
                        {statusLabel}
                    </span>
                    <span style={codeTypeStyle}>{codeTypeLabel}</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button
                        className={saveButtonClass}
                        style={saveButtonStyle}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saveButtonLabel}
                    </button>

                    {batch?.status === "approved" && (
                        <>
                            <span
                                className="vs-action-icon"
                                title="Share batch via email"
                                onClick={() => setShareOpen(true)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="18" cy="5" r="3" />
                                    <circle cx="6" cy="12" r="3" />
                                    <circle cx="18" cy="19" r="3" />
                                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                </svg>
                            </span>
                            <span
                                className="vs-action-icon"
                                title="Download batch codes CSV"
                                onClick={() =>
                                    handleExportorderBatchcode(
                                        batch?._id,
                                        order.brandId?.name,
                                        "batch name",
                                        batch
                                    )
                                }
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </span>
                        </>
                    )}

                    {(
                        <span
                            className="vs-action-icon destructive"
                            title={isPending ? "Delete batch" : "Delete approved batch (requires PIN)"}
                            onClick={() => onConfirmDelete(batch._id)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                        </span>
                    )}
                </div>
            </div>

            <ShareBatchModal
                isOpen={shareOpen}
                toggle={() => setShareOpen(false)}
                batch={batch}
                brandName={order?.brandId?.name}
            />
        </div>
    );
};

export default BatchCard;
